import type { GeneratedModifier } from "building-types/modifiers/generatedModifier";
import type { EvaluatedRoof, RoofSystem, RoofSystemInternal } from "building-types/roofs/roofSystemTypes";
import type { RWTable, Table } from "common/data/table";
import {
  ARCHETYPE_INSTANCE_CHAIN_KEY,
  archetypeEntityIdFromInstanceScopedId,
  composeComponentInstanceChain,
  EMPTY_INSTANCE_CHAIN,
} from "doc/entity/componentInstanceEntityResolve";
import { isVirtualRoofBlock, roofSystemInternalNew } from "doc/roofSystemTools";
import { getEntityEdgeModifiersTable, polyLine2Modify, type EdgeModifiersByParentId } from "doc/utils/modifierUtils";
import {
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2Shift,
} from "geom/polyLine2";

export function roofSystemToInternal(
  roofSystem: RoofSystem,
  roofs: Table<EvaluatedRoof>,
  mirror: boolean,
  mirrorTranslationX: number,
  modifiers: Table<GeneratedModifier>,
  edgeModifiersByParentId: EdgeModifiersByParentId,
  modifiersByInstanceChain: Table<Table<GeneratedModifier>>,
): RoofSystemInternal {
  const multiGableSubRoofs: RWTable<EvaluatedRoof> = {};

  for (const subroofId in roofs) {
    let subroof = roofs[subroofId];
    if (subroof.parentId !== roofSystem.id) {
      continue;
    }

    if (isVirtualRoofBlock(subroof.blocks)) {
      const { perimeter, edgeIds } = subroof.blocks;
      const archetypeId = archetypeEntityIdFromInstanceScopedId(subroofId);
      const roofEdgeModifiers = getEntityEdgeModifiersTable(edgeModifiersByParentId, archetypeId);
      const componentInstanceChain = subroof.componentInstanceChain ?? EMPTY_INSTANCE_CHAIN;
      const effectiveModifiers =
        modifiersByInstanceChain[composeComponentInstanceChain(componentInstanceChain)] ??
        modifiersByInstanceChain[ARCHETYPE_INSTANCE_CHAIN_KEY] ??
        modifiers;
      const finalPerimeter = polyLine2Modify(perimeter, roofEdgeModifiers, effectiveModifiers, edgeIds);
      const geom = polyLine2Copy(finalPerimeter);
      subroof = { ...subroof, blocks: { ...subroof.blocks, perimeter: geom } };
    }

    if (isVirtualRoofBlock(subroof.blocks) && mirror) {
      const geom = polyLine2ReflectOverYAxis(polyLine2Copy(subroof.blocks.perimeter));
      polyLine2IsClosed(geom) ? polyLine2Shift(polyLine2Reverse(geom), 1) : polyLine2Reverse(geom);
      polyLine2Move(geom, mirrorTranslationX, 0);

      subroof = { ...subroof, blocks: { ...subroof.blocks, perimeter: geom } };
    }

    // roofs w/ an empty or unknown type field are passed through unmodified
    multiGableSubRoofs[subroof.id] = subroof;
  }

  return roofSystemInternalNew(roofSystem.id, roofSystem.name, multiGableSubRoofs);
}
